"use client";
import React, { KeyboardEvent, ReactNode, useCallback, useRef, useState } from "react";
import s from "./InputNumber.module.scss";

import cn from "classnames";
import { value } from "dom7";

export type InputNumberProps = {
  name: string;
  min: number;
  max: number;
  step?: number;
  controlsStep?: number;
  initialValue?: string;
  onChange?: (value?: number | undefined) => void;
  placeholder?: string;
  showControls?: boolean;
  noStyle?: boolean;
  className?: string;
  label?: ReactNode;
  error?: boolean;
};

export const InputNumber = ({
  min,
  max,
  step = 1,
  name,
  initialValue,
  onChange,
  placeholder,
  showControls = true,
  noStyle,
  className,
  label,
  error,
  controlsStep,
}: InputNumberProps) => {
  const [inputValue, setInputValue] = useState<string | undefined>(initialValue);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (value: string) => {
      setInputValue(value);
      if (inputRef.current) inputRef.current.value = value;

      const valueAsNumber = Number(value);

      if (!valueAsNumber) {
        onChange?.(undefined);
      } else if (valueAsNumber >= min && valueAsNumber <= max) {
        onChange?.(valueAsNumber);
      }
    },
    [max, min, onChange]
  );

  const minDisabled = (Number(inputValue) || 0) <= min;

  const plusDisabled = Number(inputValue) >= max;

  const handleKeyDown = (event: KeyboardEvent) => {
    const forbiddenKeys = ["+", "-", "e", "."];

    if (forbiddenKeys.includes(event.key)) event.preventDefault();
  };

  const handleBlur = useCallback(() => {
    if (!inputValue?.trim()) {
      handleChange("");
      return;
    }
    const value = Number(inputValue);

    // fix step
    const div = value % step;
    const diff = step - div;
    const fixedValue = value < min ? min : value > max ? max : div ? value + diff : value;

    handleChange(String(fixedValue));
  }, [inputValue, max, min, step, handleChange]);

  return (
    <label className={cn(s.wrapper, { [s.noStyle]: noStyle, [s.inputError]: !!error }, className)}>
      {label && <span className={cn(s.inputLabel, "inputLabel")}>{label}</span>}
      <span className={s.inner}>
        {showControls && (
          <button
            type="button"
            aria-label="minus"
            className={cn(s.btn, s.btnMinus)}
            disabled={minDisabled}
            onClick={() => {
              const nextValue = Number(inputValue) - (controlsStep || step);
              const newValue = nextValue > min ? nextValue : min;
              handleChange(String(newValue));
            }}
          />
        )}
        <input
          type="number"
          min={min}
          max={max}
          name={name}
          defaultValue={initialValue}
          className={s.input}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          ref={inputRef}
          placeholder={placeholder}
        />
        {showControls && (
          <button
            type="button"
            aria-label="plus"
            className={cn(s.btn, s.btnPlus)}
            disabled={plusDisabled}
            onClick={() => {
              const nextValue = (Number(inputValue) || 0) + (controlsStep || step || min);

              console.log("nextValue", nextValue);

              const newValue = nextValue < max ? nextValue : max;
              handleChange(String(newValue));
            }}
          />
        )}
      </span>
    </label>
  );
};
